import { useClient } from "@/features/clients/hooks/clientQueries";
import type { Client } from "@/features/clients/types/clientTypes";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useServices } from "@/features/services/hooks/serviceQueries";
import { getServiceName } from "@/features/services/utils/getServiceName";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { HighlightedText } from "@/shared/components/ui/HighlightedText";
import { formatBusinessDate, formatBusinessTime, formatMoney } from "@/shared/utils/formatters";
import type { Appointment } from "../types/appointmentTypes";
import { AppointmentStatusBadge } from "./AppointmentStatusBadge";

interface AppointmentListCardProps {
  appointment: Appointment;
  onClick: (appointment: Appointment) => void;
  client?: Pick<Client, "firstName" | "lastName"> | null;
  searchText?: string;
  showDate?: boolean;
  showPrice?: boolean;
}

export function AppointmentListCard({ appointment, onClick, client: suppliedClient, searchText = "", showDate = false, showPrice = false }: AppointmentListCardProps) {
  const services = useServices();
  const { business } = useCurrentBusiness();
  const client = useClient(suppliedClient === undefined ? appointment.clientId ?? "" : "");
  const customer = suppliedClient === undefined ? client.data : suppliedClient;
  const zone = business?.timezone ?? "UTC";
  const currency = appointment.billingCurrency ?? business?.currency;
  const bookedDuration = appointment.services.length > 0 && appointment.services.every((line) =>
    line.durationMinutes !== null && Number.isFinite(line.durationMinutes) && line.durationMinutes > 0)
    ? appointment.services.reduce((total, line) => total + (line.durationMinutes ?? 0), 0) : null;
  const duration = bookedDuration ?? (appointment.startAt && appointment.endAt
    ? Math.round((appointment.endAt.getTime() - appointment.startAt.getTime()) / 60_000) : null);
  if (suppliedClient === undefined && client.error) return <ErrorState message={client.error.message} onRetry={() => void client.refetch()} />;
  if (services.error) return <ErrorState message={services.error.message} onRetry={() => void services.refetch()} />;
  return (
    <InfoRow
      isLoading={(suppliedClient === undefined && client.isLoading) || services.isLoading}
      title={customer ? [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "Customer" : "Customer unavailable"}
      subtitle={<HighlightedText text={getServiceName(appointment, services.data)} search={searchText} />}
      searchText={searchText}
      caption={[
        showDate && appointment.startAt ? formatBusinessDate(appointment.startAt, zone, { day: "numeric", month: "short", year: "numeric" }) : null,
        duration !== null ? `${duration} min` : "Time not set",
        showPrice ? appointment.price === null ? "Price not set" : currency ? formatMoney(appointment.price, currency) : "Currency unavailable" : null,
      ].filter(Boolean).join(" · ")}
      icon={<span className="rounded-xl bg-matcha px-2 py-3 text-xs font-bold text-primary">{appointment.startAt ? formatBusinessTime(appointment.startAt, zone) : "—"}</span>}
      trailing={<AppointmentStatusBadge status={appointment.status} />}
      onClick={() => onClick(appointment)}
      bold
      className="rounded-2xl border border-border bg-card"
    />
  );
}
